import '@ant-design/compatible/assets/index.css';
import { useQuery } from 'react-query';
import React, { useEffect, useMemo } from 'react';

import { Radio, Col, Spin, Row, Divider, Tag } from 'antd';
import DF from 'DisplayFramework';
import FormInput from 'components/FormInput';
import { getFinModificationByModid } from 'services/fincontract';
import { FormInstance } from 'antd/lib/form';
import TextArea from 'antd/lib/input/TextArea';
import { toCurrency } from 'utils/utils';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import UtinaCard from 'components/UtinaCard';
import GetLabel from 'DisplayFramework/components/GetLabel';

export type TabFinModificationReviewProps = {
  objectid: number;
  subentityid: number;
  form?: FormInstance;
  setSuccessful?: any;
  hidefields?: any;
  isFetching: any;
  isActive?: boolean;
};

const TabFinModificationReview = ({
  objectid,
  subentityid,
  form,
  setSuccessful,
  hidefields,
  isFetching,
  isActive
}: TabFinModificationReviewProps) => {
  const { data: finmod, isFetching: isisFetchingFinMod } = useQuery(
    ['getFinModificationByModid', { modid: subentityid }, isFetching],
    () =>
      getFinModificationByModid({ modid: subentityid }).then((res) => {
        return res.data;
      }),
    { refetchOnMount: true}
  );

  useEffect(() => {
    if (setSuccessful !== undefined) {
      setSuccessful(isisFetchingFinMod);
    }
  }, [isisFetchingFinMod]);

  const optionsMode = [
    { label: <GetLabel label="Cancel" />, value: 'CANCEL' },
    { label: <GetLabel label="Validate" />, value: 'VALID' },
    { label: <GetLabel label="Refuse" />, value: 'REFUS' },
  ];

  const COLUMNS: DataGridColDef<any>[] = [
    {
      dataIndex: 'assetreference',
      width: 120,
    },
    {
      dataIndex: 'assetlabel',
      width: 150,
    },
    {
      dataIndex: 'targetphase',
      width: 120,
    },
    {
      dataIndex: 'targetstatus',
      width: 120,
    },
    {
      dataIndex: 'finrvamount',
      render: (val, record) => toCurrency(val, finmod?.currcode),
      width: 120,
    },
    {
      dataIndex: 'irvamount',
      render: (val, record) => toCurrency(val, finmod?.currcode),

      width: 120,
    },
    {
      dataIndex: 'damage',
      width: 100,
    },
    {
      dataIndex: 'degfeesamount',
      render: (val, record) => toCurrency(val, finmod?.currcode),

      width: 130,
    },
    {
      dataIndex: 'spvalue',
      width: 120,
    },
    {
      dataIndex: 'spamount',
      width: 100,
      render: (val, record) => toCurrency(val, finmod?.currcode),
    },
    {
      dataIndex: 'tpofamount',
      width: 120,
      render: (val, record) => toCurrency(val, finmod?.currcode),
    },
    {
      dataIndex: 'saleamount',
      width: 120,
      render: (val, record) => toCurrency(val, finmod?.currcode),
    },
    {
      dataIndex: 'feeslabel',
      width: 120,
    },

    {
      dataIndex: 'feesamount',
      width: 120,
      render: (val, record) => toCurrency(val, finmod?.currcode),
    },
    {
      dataIndex: 'totalinvoiceamount',
      width: 200,
      render: (val, record) => toCurrency(val, finmod?.currcode),
      fixed: 'right',
    },
  ];

  const COLUMNS_Element: DataGridColDef<any>[] = [
    {
      dataIndex: 'cteinvestamount',
      render: (val, record) => toCurrency(val, finmod?.currcode),
      width: 120,
    },
    {
      dataIndex: 'outstandingamount',
      render: (val, record) => toCurrency(val, finmod?.currcode),
      width: 150,
    },
    {
      dataIndex: 'newoutstandingamount',
      render: (val, record) => toCurrency(val, finmod?.currcode),
      width: 120,
    },
    {
      dataIndex: 'ctestartdate',
      width: 120,
    },
    {
      dataIndex: 'cteenddate',
      width: 120,
    },
    {
      dataIndex: 'newcteenddate',
      width: 120,
    },
    {
      dataIndex: 'lastinvoicedate',
      width: 100,
    },
    {
      dataIndex: 'nbdaysinterest',

      width: 130,
    },
    {
      dataIndex: 'nominalrate',
      width: 120,
    },

    {
      dataIndex: 'cteduration',
      width: 120,
    },

    {
      dataIndex: 'calculatedinterest',
      width: 120,
    },
  ];

  const columns_: any = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        comcode: 'prilabel',
        dataIndex: 'prilabel',
        width: '30%',
        render: (val, record) => (
          <span
            style={{
              color: record?.servtargetstatus === 'STOP' ? 'red' : 'black',
            }}
          >
            {val}
          </span>
        ),
      },
      {
        comcode: 'oldoverallduration',
        dataIndex: 'oldoverallduration',
        render: (value, record) => value,
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'overallduration',
        dataIndex: 'overallduration',
        render: (value, record) => value,
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'oldremainingduration',
        dataIndex: 'oldremainingduration',
        render: (value, record) => value,
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'remainingduration',
        dataIndex: 'remainingduration',
        render: (value, record) => value,
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'oldservbasisamount',
        dataIndex: 'oldservbasisamount',
        render: (value, record) => toCurrency(value),
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'servbasisamount',
        dataIndex: 'servbasisamount',
        render: (value, record) => toCurrency(value),
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'oldservfixedamount',
        dataIndex: 'oldservfixedamount',
        render: (value, record) => toCurrency(value),
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'servfixedamount',
        dataIndex: 'servfixedamount',
        render: (value, record) => toCurrency(value),
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'oldservpercentage',
        dataIndex: 'oldservpercentage',
        render: (value, record) => toCurrency(value),
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'servpercentage',
        dataIndex: 'servpercentage',
        render: (value, record) => toCurrency(value),
        width: '20%',
        align: 'right',
      },

      {
        comcode: 'status',
        dataIndex: 'status',
        render: (value, record) => {
          return (
            <>
              {record?.servtargetstatus === 'NEW' && (
                <Tag color="#87d068">{record.servtargetstatus}</Tag>
              )}
              {record?.servtargetstatus === 'UPDATE' && (
                <Tag color="#2db7f5">{record?.servtargetstatus}</Tag>
              )}
              {record?.servtargetstatus === 'STOP' && (
                <Tag color="#cd201f"> {record?.servtargetstatus}</Tag>
              )}
            </>
          );
        },
        fixed: 'right',
        width: '20%',
        align: 'right',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <DF.Provider pagcode="tabfinmodreview">
      <Spin spinning={isisFetchingFinMod} delay={500}>
        <Row>
          <Col span={24}>
            <UtinaCard comcode="Event" level={5}>
              <Row gutter={[16, 16]}>
                <Col span={2} style={{ fontWeight: 'bold' }}>
                  <GetLabel label="Event :" />{' '}
                </Col>
                <Col span={4}>{finmod?.modeventlabel}</Col>
                <Col span={2} style={{ fontWeight: 'bold' }}>
                  <GetLabel label="Contract :" />{' '}
                </Col>
                <Col span={8}>
                  {finmod?.ctreference + ' - ' + finmod?.ctdescription}{' '}
                </Col>
                <Col span={2} style={{ fontWeight: 'bold' }}>
                  <GetLabel label="Date :" />{' '}
                </Col>
                <Col span={4}>{finmod?.creationdate?.substring(0, 10)}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={2} style={{ fontWeight: 'bold' }}>
                  <GetLabel label="User : " />
                </Col>
                <Col span={4}>{finmod?.creationusername}</Col>
                <Col span={2} style={{ fontWeight: 'bold' }}>
                  <GetLabel label="Comment : " />
                </Col>
                <Col span={4}>{finmod?.modcomment}</Col>
              </Row>
            </UtinaCard>

            <UtinaCard comcode="Modified Data" level={5}>
              <Row>
                <Col span={24}>
                  <Row
                    style={{
                      fontWeight: 'bold',
                      background: '#fff',
                      padding: '8px 12px',
                      boxShadow: ' 0px 0px 7px rgba(0, 0, 0, 0.09)',
                      borderRadius: '7px',
                    }}
                  >
                    <Col span={8}>
                      <GetLabel label="Description" />
                    </Col>
                    <Col span={4}>
                      <GetLabel label="Old value" />
                    </Col>
                    <Col span={4}>
                      <GetLabel label="New value" />
                    </Col>
                    <Col span={4}>
                      <GetLabel label="Comment" />
                    </Col>
                  </Row>
                  {finmod?.simulParams?.map((el: any, index) => {
                    let color;
                    if (index % 2) {
                      color = 'white';
                    } else {
                      color = '#F5EFE6';
                    }

                    return (
                      <div key={index}>
                        {el.parformat !== 'LIST' ? (
                          <Row
                            className={
                              index % 2 ? 'rowColorTabModifReview' : 'white'
                            }
                            style={{
                              padding: '5px 8px',
                            }}
                          >
                            <Col span={8}>
                              {el.flagHide
                                ? '* ' +
                                  el.parcode +
                                  ' ________ ' +
                                  el.entitydescription
                                : el.parcodelabel ?? el.entitydescription}
                            </Col>
                            <Col span={4}>
                              {el.parformat === 'CURRENCY'
                                ? toCurrency(el.oldvalue, finmod.currcode)
                                : el.oldvalue}
                            </Col>
                            <Col span={4}>
                              {el.parformat === 'CURRENCY'
                                ? toCurrency(el.newvalue, finmod.currcode)
                                : el.newvalue}
                            </Col>
                            <Col span={4}>{el.parcomment}</Col>
                          </Row>
                        ) : el.parcode === 'ELEMENTS' ? (
                          <div>
                            <Divider orientation="left">
                              List of Elements :
                            </Divider>
                            <DataGrid
                              dataSource={el.finModSimulParamElements}
                              columns={
                                finmod.modevent === 'SERVRESCH'
                                  ? columns_
                                  : COLUMNS_Element
                              }
                              scroll={{ x: 1300, y: 360 }}
                              pagination={{ pageSize: 200 }}
                            />
                          </div>
                        ) : (
                          <div>
                            <Divider orientation="left">
                              List of Assets :
                            </Divider>
                            <DataGrid
                              dataSource={el.finModSimulParamAssets}
                              columns={COLUMNS}
                              scroll={{ x: 1300, y: 360 }}
                              pagination={{ pageSize: 200 }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Col>
              </Row>
            </UtinaCard>
          </Col>
        </Row>
        {hidefields ? (
          ''
        ) : (
          <UtinaCard comcode="Review Decision" level={5}>
            <Row gutter={[8, 8]}>
              <FormInput
                col
                xs={24}
                name="status"
                comcode="decision"
                rules={[{ required: true }]}
                initialValue="CANCEL"
              >
                <Radio.Group
                  options={optionsMode}
                  defaultValue="CANCEL"
                  optionType="button"
                />
              </FormInput>

              <FormInput
                col
                xs={24}
                name="comment"
                rules={[{ required: true }]}
              >
                <TextArea rows={4} />
              </FormInput>
            </Row>
          </UtinaCard>
        )}
      </Spin>
    </DF.Provider>
  );
};
export default TabFinModificationReview;
